// helpers/useLoanFilters.ts
import {useCallback} from 'react';
import {useSearchParams} from 'react-router-dom';

export function useParamsFilter() {
  const [searchParams, setSearchParams] = useSearchParams();

  const getFilter = useCallback(
    (key: string): string => {
      return searchParams.get(key) || '';
    },
    [searchParams],
  );

  const setFilter = useCallback(
    (key: string, value: string) => {
      setSearchParams(
        prev => {
          const newParams = new URLSearchParams(prev);
          if (value) {
            newParams.set(key, value);
          } else {
            newParams.delete(key);
          }
          return newParams;
        },
        {replace: true},
      );
    },
    [setSearchParams],
  );

  const getFilters = useCallback(
    (keys: string[]): Record<string, string> => {
      return keys.reduce(
        (acc, key) => ({
          ...acc,
          [key]: searchParams.get(key) || '',
        }),
        {},
      );
    },
    [searchParams],
  );

  const setFilters = useCallback(
    (filters: Record<string, string>) => {
      setSearchParams(
        prev => {
          const newParams = new URLSearchParams(prev);
          Object.entries(filters).forEach(([key, value]) => {
            if (value) {
              newParams.set(key, value);
            } else {
              newParams.delete(key);
            }
          });
          return newParams;
        },
        {replace: true},
      );
    },
    [setSearchParams],
  );

  const resetFilters = useCallback(
    (keys: string[]) => {
      setSearchParams(
        prev => {
          const newParams = new URLSearchParams(prev);
          keys.forEach(key => newParams.delete(key));
          return newParams;
        },
        {replace: true},
      );
    },
    [setSearchParams],
  );

  const hasFilters = useCallback(
    (keys: string[]): boolean => {
      return keys.some(key => Boolean(searchParams.get(key)));
    },
    [searchParams],
  );

  return {
    getFilter,
    setFilter,
    getFilters,
    setFilters,
    resetFilters,
    hasFilters,
    searchParams,
  };
}
